/* Phase 5 · jp-collection.js — progressive enhancement only.
   Filters/sort are plain GET forms (work with no JS); filter changes batch
   behind an explicit Apply action while sort remains immediate. P0.5:
   shopify:section:load re-init (Customiser
   re-renders replace the section DOM and kill bindings). */
(() => {
  if (window.__jpCollectionInit) return;
  window.__jpCollectionInit = true;
  const privacy = () => window.JapanScissors && window.JapanScissors.privacy;
  const readPreference = (key) => {
    const store = privacy();
    return store ? store.getPreference(key) : null;
  };
  const writePreference = (key, value) => {
    const store = privacy();
    if (store) store.setPreference(key, value);
  };

  /* persona size-rec note in the size facet: show the active persona's recommended
     blade length (data-map = {salon,barber,student}). Source of truth is the body
     persona-<id> class set by jp-persona.js; updates on persona:changed. Visual-only —
     never touches the filter inputs. */
  const currentPersona = () => (document.body.className.match(/\bpersona-(all|salon|barber|student)\b/) || [null, 'all'])[1];
  const parseSizeNumbers = (value) => {
    const normalized = String(value || '').replace(/(\d),(\d)/g, '$1.$2');
    const matches = normalized.match(/\d+(?:\.\d+)?/g) || [];
    return matches.map(Number).filter(Number.isFinite);
  };
  let filterMediaQuery;

  const updateSizeRec = () => {
    const p = currentPersona();
    document.querySelectorAll('[data-jp-sizerec]').forEach((el) => {
      let map = {};
      try { map = JSON.parse(el.dataset.map || '{}'); } catch (e) { /* malformed map → hide */ }
      const range = (p && p !== 'all' && map[p]) ? map[p] : '';
      const out = el.querySelector('[data-jp-sizerec-val]');
      const applyBtn = el.querySelector('[data-jp-sizerec-apply]');
      if (range) { if (out) out.textContent = range; el.hidden = false; if (applyBtn) applyBtn.hidden = false; }
      else { el.hidden = true; if (applyBtn) applyBtn.hidden = true; }
    });
  };

  /* S33 · grid⇄list view toggle — progressive enhancement. Flips a .jp-pgrid--list class on the
     grid (CSS restyles the SAME card into rows — no second renderer) for this page only.
     aria-pressed on the two real buttons stays in sync. Fires jp:track
     jp_collection_view_changed on user action only. */
  // View mode is deliberately page-scoped. Restoring a preference after
  // consent resolves would reflow an already painted grid for returning users.
  const readView = () => document.documentElement.classList.contains('jp-collection-view-list') ? 'list' : 'grid';
  const applyView = (root, view, opts) => {
    const scope = root || document;
    document.documentElement.classList.toggle('jp-collection-view-list', view === 'list');
    scope.querySelectorAll('[data-jp-grid]').forEach((grid) => {
      grid.classList.toggle('jp-pgrid--list', view === 'list');
    });
    scope.querySelectorAll('[data-jp-view]').forEach((group) => {
      group.querySelectorAll('[data-jp-view-set]').forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.dataset.jpViewSet === view ? 'true' : 'false');
      });
    });
    if (opts && opts.track) {
      try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_collection_view_changed', view: view } })); } catch (e) { /* no-op */ }
    }
  };
  const initView = (root) => {
    /* mark the toggle usable (CSS reveals it only when JS is present — no-JS keeps the default grid) */
    root.querySelectorAll('[data-jp-view]').forEach((group) => { group.dataset.jpViewReady = 'true'; });
    root.querySelectorAll('[data-jp-view-set]').forEach((btn) => {
      if (btn.dataset.jpBound) return;
      btn.dataset.jpBound = 'true';
      btn.addEventListener('click', () => {
        applyView(document, btn.dataset.jpViewSet, { track: true });
      });
    });
    /* Reflect the shopper's current-page choice after a section rerender. */
    applyView(root, readView(), {});
  };

  const initScope = (scope) => {
    const root = scope || document;
    /* If the shopper requested filters before this deferred controller arrived,
       keep the native inline form they were shown. A late bundle must not turn
       that user-selected fallback back into an off-canvas layer. */
    if (document.documentElement.classList.contains('jp-collection-fallback')) return;

    /* A hidden Price facet is not enough: Shopify applies incoming price
       parameters before Liquid renders. When pricing is blocked, remove any
       crafted/stale price parameters and reload the canonical unfiltered URL
       so the visible result count and controls cannot conceal active state. */
    const pricingScope = root.matches?.('[data-jp-market-pricing-allowed]')
      ? root
      : root.querySelector?.('[data-jp-market-pricing-allowed]');
    if (pricingScope && pricingScope.dataset.jpMarketPricingAllowed !== 'true') {
      const url = new URL(window.location.href);
      let removedPrice = false;
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.indexOf('filter.v.price.') === 0) {
          url.searchParams.delete(key);
          removedPrice = true;
        }
      });
      if (removedPrice) {
        window.location.replace(url.pathname + (url.search ? url.search : '') + url.hash);
        return;
      }
    }

    /* mobile: the filter rail is an off-canvas dialog (jp-layers handles open/close/trap/scrim/
       focus-return via data-jp-open/-close/-layer). Apply dialog semantics ONLY at the mobile
       breakpoint — the desktop sidebar and the no-JS inline panel must not be a dialog; CSS
       visibility:hidden keeps the closed drawer out of the tab order + a11y tree. */
    root.querySelectorAll('.jp-col__rail-panel').forEach((panel) => {
      if (!filterMediaQuery) {
        filterMediaQuery = window.matchMedia('(max-width: 989px)');
        /* Resolve current panels on each breakpoint change. Per-panel media
           listeners retain removed Customiser sections and can act on a new
           layer that reuses the same ID. One shared listener owns the viewport. */
        filterMediaQuery.addEventListener('change', (e) => {
          document.querySelectorAll('.jp-col__rail-panel').forEach((current) => {
            if (!e.matches && current.classList.contains('is-open') && window.JapanScissors && window.JapanScissors.layers) {
              window.JapanScissors.layers.close(current.getAttribute('data-jp-layer'));
            }
            if (current.__jpSetDialog) current.__jpSetDialog();
          });
        });
      }
      panel.__jpSetDialog = () => {
        if (filterMediaQuery.matches) {
          panel.setAttribute('role', 'dialog');
          panel.setAttribute('aria-modal', 'true');
          panel.setAttribute('aria-hidden', panel.classList.contains('is-open') ? 'false' : 'true');
        } else {
          panel.setAttribute('role', 'region');
          panel.removeAttribute('aria-modal');
          panel.removeAttribute('aria-hidden');
        }
      };
      panel.__jpSetDialog();
    });

    root.querySelectorAll('[data-jp-filter-form]').forEach((form) => {
      if (form.dataset.jpBound) return;
      form.dataset.jpBound = 'true';
      let t;
      form.addEventListener('change', (e) => {
        if (e.target.matches('select[name="sort_by"]')) {
          const pageScope = form.closest('[data-jp-rail-scope]') || document;
          const facetForm = [...pageScope.querySelectorAll('form[data-jp-filter-form]')]
            .find((candidate) => candidate !== form && candidate.querySelector('.jp-col__rail-panel'));
          if (facetForm) {
            let sortInput = facetForm.querySelector('input[name="sort_by"]');
            if (!sortInput) {
              sortInput = document.createElement('input');
              sortInput.type = 'hidden';
              sortInput.name = 'sort_by';
              facetForm.appendChild(sortInput);
            }
            sortInput.value = e.target.value;
            facetForm.submit();
            return;
          }
        }
        /* Batch every facet change behind Apply. This avoids a full page reload
           per checkbox on desktop and already matches the mobile drawer model. */
        if (e.target.closest('.jp-col__rail-panel')) return;
        if (e.target.matches('input[type="number"]')) {
          window.clearTimeout(t);
          t = setTimeout(() => form.submit(), 900);
          return;
        }
        form.submit();
      });
    });

    /* (sort/count toolbar sticky-pin removed — it floated over content when the header hid on scroll) */

    /* below-grid SEO description: clamp + read-more (progressive enhancement —
       full text is always in the DOM; we only collapse it when it overflows) */
    root.querySelectorAll('[data-jp-readmore]').forEach((box) => {
      if (box.dataset.jpBound) return;
      box.dataset.jpBound = 'true';
      const btn = box.parentElement.querySelector('[data-jp-readmore-btn]');
      if (!btn) return;
      const labels = btn.querySelector('span');
      const moreTxt = labels ? labels.textContent : 'Read more';
      const lessTxt = btn.dataset.less || moreTxt.replace(/more/i, 'less');
      const measure = () => {
        /* A reader can expand during the debounced resize. Recheck here so
           that queued measurement cannot clamp their explicit choice. */
        if (box.classList.contains('is-open')) return;
        /* Reapply the measuring constraint after a wider view removed it.
           Otherwise scrollHeight equals clientHeight forever and a later
           narrow viewport can never recover its Read more control. */
        box.classList.add('is-clamped');
        // only clamp when the content is meaningfully taller than the clamp
        if (box.scrollHeight - box.clientHeight > 24) {
          box.classList.add('is-clamped');
          btn.hidden = false;
        } else {
          box.classList.remove('is-clamped');
          btn.hidden = true;
        }
      };
      box.classList.add('is-clamped');
      measure();
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-open');
        box.classList.toggle('is-clamped', !open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (labels) labels.textContent = open ? lessTxt : moreTxt;
      });
      let rt;
      box.__jpRemeasure = () => {
        if (box.classList.contains('is-open')) return;
        window.clearTimeout(rt);
        rt = setTimeout(measure, 200);
      };
    });

    updateSizeRec();
    initView(root);
    personaNudge(root);
    /* Enhancement CSS must never hide the native GET form until every controller
       above has initialized successfully. A thrown/blocked bundle leaves the
       server-rendered form and Apply button fully usable. */
    document.documentElement.classList.add('jp-collection-ready');
    document.documentElement.classList.remove('jp-collection-pending');
  };

  /* 2f · persona nudge — on a REPEAT collection view, if the store is still Everyone and the nudge hasn't been
     dismissed, clone the Liquid template into the grid after the first row. Choosing a chip tunes the store and
     CSS auto-hides the card; dismiss is remembered forever. Never a modal. */
  const personaNudge = (root) => {
    const grid = (root && root.querySelector('.jp-col__grid')) || document.querySelector('.jp-col__grid');
    const tmpl = document.querySelector('[data-jp-nudge-tmpl]');
    if (!grid || !tmpl || !tmpl.content) return;
    if (!window.__jpColViewCounted) {
      window.__jpColViewCounted = true;
      writePreference('jp_col_views', String((parseInt(readPreference('jp_col_views') || '0', 10) || 0) + 1));
    }
    const views = parseInt(readPreference('jp_col_views') || '1', 10) || 1;
    const dismissed = readPreference('jp_nudge_dismissed') === '1';
    if (dismissed || views < 2) return;
    if (!document.body.classList.contains('persona-all')) return;
    if (grid.querySelector('[data-jp-nudge]')) return;
    const card = tmpl.content.firstElementChild && tmpl.content.firstElementChild.cloneNode(true);
    if (!card) return;
    /* anchor after the 6th PRODUCT card — a clean row boundary on both the 2-col and 3-col grids, and past the
       promo tile (selecting by .jp-pcard skips the promo + the nudge itself). Fewer than 7 cards → append. */
    const cards = grid.querySelectorAll('.jp-pcard');
    grid.insertBefore(card, cards[6] || null);
  };

  /* dismiss (delegated, once) — remember forever + drop the card. */
  document.addEventListener('click', (e) => {
    const x = e.target.closest('[data-jp-nudge-dismiss]');
    if (!x) return;
    writePreference('jp_nudge_dismissed', '1');
    const card = x.closest('[data-jp-nudge]');
    if (card) card.remove();
    try { document.dispatchEvent(new CustomEvent('jp:track', { detail: { event: 'jp_nudge_dismissed' } })); } catch (e) { /* no-op */ }
  });

  /* one shared resize listener (added once) re-measures every current read-more
     box — avoids per-box listener accumulation across Customiser re-renders */
  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-jp-readmore]').forEach((b) => { if (b.__jpRemeasure) b.__jpRemeasure(); });
    document.querySelectorAll('.jp-col__rail-panel').forEach((p) => { if (p.__jpSetDialog) p.__jpSetDialog(); });
  }, { passive: true });

  document.addEventListener('persona:changed', updateSizeRec);
  document.addEventListener('jp:privacy:change', (e) => {
    /* View is intentionally page-scoped. A privacy change must never reapply
       layout after paint: grid-to-list hydration is a large CLS. */
    document.querySelectorAll('[data-jp-nudge]').forEach((card) => card.remove());
    if (e.detail && e.detail.preferences) {
      window.__jpColViewCounted = false;
      personaNudge(document);
    }
  });

  /* P1.7 · "Show my range" — one-tap apply the active persona's recommended blade lengths.
     Parses the numbers out of the shown range (e.g. "6.5″–7.0″" → 6.5‥7.0), checks every
     in-range, available size checkbox in that facet, then submits the GET form. No-op if the
     range or facet can't be resolved. Added once (guarded IIFE) — never touches state directly. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-jp-sizerec-apply]');
    if (!btn) return;
    const note = btn.closest('[data-jp-sizerec]');
    /* A4: the button lives in the facet (closest details) OR the header row (outside the form). In the
       header case, resolve the size facet + its form by the [data-jp-sizefacet] marker. */
    const details = btn.closest('details') || document.querySelector('[data-jp-sizefacet]');
    const form = (details && details.closest('form')) || btn.closest('form');
    const valEl = note && note.querySelector('[data-jp-sizerec-val]');
    const vals = parseSizeNumbers((valEl && valEl.textContent) || '');
    if (!details || !form || !vals.length) return; /* no facet/form/range → let the <a> fallback navigate */
    e.preventDefault();
    const lo = Math.min.apply(null, vals);
    const hi = Math.max.apply(null, vals);
    details.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      const checkboxSizes = parseSizeNumbers(cb.value);
      const inRange = checkboxSizes.some((n) => n >= lo && n <= hi);
      /* Applying a persona range replaces the current size selection: stale
         out-of-range and non-numeric size values must not survive the submit. */
      cb.checked = inRange && !cb.disabled;
    });
    form.submit(); /* navigate to the canonical filtered URL (back-safe, shareable) */
  });

  document.addEventListener('DOMContentLoaded', () => initScope());
  document.addEventListener('shopify:section:load', (e) => initScope(e.target));
})();
